import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { galleryImages } from '../data/mock';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const Gallery = () => {
  const { t } = useLanguage();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Minimum swipe distance
  const minSwipeDistance = 50;

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'unset';
  };

  const nextImage = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % galleryImages.length);
  }, []);

  const prevImage = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, nextImage, prevImage]);

  // Touch handlers for swipe
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      nextImage();
    } else if (isRightSwipe) {
      prevImage();
    }
  };

  // Image captions
  const captions = {
    ko: [
      '루프탑 불멍 파티',
      '루프탑 반려견과 불멍',
      '눈 오는 날 불멍',
      '루프탑 발코니',
      '프라이빗 바 라운지',
      '게이밍 라운지',
      '포커 나이트',
      'UFC 시청 파티',
      '포커 & 보드게임',
      '브라이덜 샤워',
      '생일 파티',
      '파티룸 전경',
      '바베큐 파티',
      '루프탑 전경',
      '건물 외관'
    ],
    en: [
      'Rooftop Firepit Party',
      'Rooftop with Dog & Firepit',
      'Snowy Firepit Night',
      'Rooftop Balcony',
      'Private Bar Lounge',
      'Gaming Lounge',
      'Poker Night',
      'UFC Viewing Party',
      'Poker & Board Games',
      'Bridal Shower',
      'Birthday Party',
      'Party Room View',
      'BBQ Party',
      'Rooftop View',
      'Building Exterior'
    ]
  };

  const { language } = useLanguage();

  return (
    <section className="bg-[#121212] py-16 md:py-24 animate-fade-in">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
          {t('galleryTitle')}
        </h2>
        <p className="text-white/60 text-center mb-10">{t('galleryCaption')}</p>
        
        {/* Gallery Grid with Lazy Loading */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {galleryImages.map((image, index) => (
            <div 
              key={index}
              className="relative overflow-hidden cursor-pointer group"
              style={{ aspectRatio: '1' }}
              onClick={() => openLightbox(index)}
            >
              <img 
                src={image} 
                alt={captions[language]?.[index] || `Gallery ${index + 1}`}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300" />
              <div className="absolute inset-0 border-2 border-transparent group-hover:border-[#00FFD1] transition-colors duration-300" />
              
              {/* Caption on hover */}
              <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <p className="text-white text-xs text-center truncate">
                  {captions[language]?.[index] || `Photo ${index + 1}`}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox with Swipe Support */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Close button */}
          <button 
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2 z-10"
            onClick={closeLightbox}
          >
            <X size={32} />
          </button>
          
          {/* Previous button */}
          <button 
            className="absolute left-2 md:left-4 text-white/80 hover:text-white p-2 z-10 bg-black/50 rounded-full"
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
          >
            <ChevronLeft size={32} />
          </button>
          
          {/* Image */}
          <div 
            className="max-w-[90vw] max-h-[85vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={galleryImages[currentIndex]} 
              alt={captions[language]?.[currentIndex] || `Gallery ${currentIndex + 1}`}
              className="max-w-full max-h-[75vh] object-contain"
            />
            {/* Caption */}
            <p className="text-white/80 mt-4 text-center">
              {captions[language]?.[currentIndex] || `Photo ${currentIndex + 1}`}
            </p>
          </div>
          
          {/* Next button */}
          <button 
            className="absolute right-2 md:right-4 text-white/80 hover:text-white p-2 z-10 bg-black/50 rounded-full"
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
          >
            <ChevronRight size={32} />
          </button>
          
          {/* Counter and swipe hint */}
          <div className="absolute bottom-4 left-0 right-0 text-center">
            <span className="text-white/60 text-sm">
              {currentIndex + 1} / {galleryImages.length}
            </span>
            <p className="text-white/40 text-xs mt-1 md:hidden">
              ← 스와이프하여 이동 →
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
